import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PortraitImg from "../assets/img/mockupPortraitBlankHeader.png";
import LandscapeImg from "../assets/img/mockupLandscape.png";

const VideoPreview = () => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [orientation, setOrientation] = useState("1080x1920");
  const [frameImage, setFrameImage] = useState(PortraitImg);
  const [containerScale, setContainerScale] = useState(1);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerCompany, setHeaderCompany] = useState(null);
  const containerRef = useRef(null);
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);

  const frameConfig = useMemo(
    () => ({
      width: 2048,
      height: 1248,
    }),
    []
  );

  const videoConfig = useMemo(
    () => ({
      "1080x1920": {
        screenWidth: 224,
        screenHeight: 366,
        leftScreenPosition: { x: 226, y: 560 },
        rightScreenPosition: { x: 1590, y: 560 },
      },
      "1920x1080": {
        screenWidth: 742,
        screenHeight: 416,
        leftScreenPosition: { x: 230, y: 65 },
        rightScreenPosition: { x: 1080, y: 65 },
      },
    }),
    []
  );

  const handleResize = useCallback(() => {
    if (containerRef.current) {
      const maxWidth = window.innerWidth * 0.9;
      const maxHeight = window.innerHeight * 0.8;
      const widthScale = maxWidth / frameConfig.width;
      const heightScale = maxHeight / frameConfig.height;
      setContainerScale(Math.min(widthScale, heightScale));
    }
  }, [frameConfig.width, frameConfig.height]);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const creativeTemplate = urlParams.get("adhesePreviewCreativeTemplate");

    if (creativeTemplate) {
      if (creativeTemplate.includes("1920x1080")) {
        setOrientation("1920x1080");
        setFrameImage(LandscapeImg);
      } else if (creativeTemplate.includes("1080x1920")) {
        setOrientation("1080x1920");
        setFrameImage(PortraitImg);
      }
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const creativeId = urlParams.get("adhesePreviewCreativeId");
    const slotId = urlParams.get("adhesePreviewSlotId");

    if (!creativeId || !slotId) return;

    const fetchPreviewVideo = async () => {
      try {
        const response = await fetch(
          `https://uncommonmedia-preview.staging-adhese.org/creatives/preview/tag.do?id=${creativeId}&slotId=${slotId}`
        );
        const data = await response.json();

        const videoAsset = data.assets?.find(
          (asset) =>
            asset?.data?.type === 501 && asset.data.url?.endsWith(".mp4")
        );

        if (videoAsset) {
          setVideoUrl(videoAsset.data.url);
        }
      } catch (error) {
        console.error("Error loading video:", error);
      }
    };

    fetchPreviewVideo();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const slotId = urlParams.get("adhesePreviewSlotId");

    if (!slotId) return;

    const fetchHeaderData = async () => {
      try {
        const response = await fetch(
          `https://ucm-image-db.lawrencesalazardev.workers.dev/images?slot_id=${slotId}`
        );
        const data = await response.json();

        if (data && data.image_path) {
          setHeaderImage(data.image_path);
          setHeaderCompany(data.company);
        } else {
          setHeaderImage(
            "https://uncommon-media.net/wp-content/uploads/2024/09/cropped-Uncommon-Media-Black-SMALL.jpg"
          );
          setHeaderCompany(null);
        }
      } catch (error) {
        setHeaderImage(
          "https://uncommon-media.net/wp-content/uploads/2024/09/cropped-Uncommon-Media-Black-SMALL.jpg"
        );
        setHeaderCompany(null);
      }
    };

    fetchHeaderData();
  }, []);

  useEffect(() => {
    const setupLooping = (videoRef) => {
      if (videoRef.current) {
        videoRef.current.loop = true;
        videoRef.current.addEventListener("ended", () => {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(() => {});
        });
      }
    };

    setupLooping(leftVideoRef);
    setupLooping(rightVideoRef);

    return () => {
      [leftVideoRef, rightVideoRef].forEach((ref) => {
        if (ref.current) {
          ref.current.removeEventListener("ended", () => {});
        }
      });
    };
  }, [videoUrl]);

  const currentVideoConfig =
    videoConfig[orientation] || videoConfig["1080x1920"];

  return (
    <div
      style={{
        padding: "1rem",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#f0f0f0",
      }}
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: `${frameConfig.width * containerScale}px`,
          height: `${frameConfig.height * containerScale}px`,
          margin: "0 auto",
        }}
      >
        {/* Header with Logo and Company */}
        {headerImage && (
          <div
            style={{
              position: "absolute",
              top: `${
                orientation === "1920x1080"
                  ? -120 * containerScale
                  : 230 * containerScale
              }px`,
              left: `${(frameConfig.width / 2 - 200) * containerScale}px`,
              width: `${400 * containerScale}px`,
              height: `${80 * containerScale}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: `${12 * containerScale}px`,
              zIndex: 3,
              borderRadius: "8px",
              padding: `${8 * containerScale}px`,
            }}
          >
            <img
              src={headerImage}
              alt="Logo"
              style={{
                height: `${100 * containerScale}px`,
                objectFit: "contain",
              }}
            />
            {headerCompany && headerCompany !== "null" && (
              <span
                style={{
                  fontSize: `${90 * containerScale}px`,
                  fontWeight: "bold",
                  color: "#000",
                  whiteSpace: "nowrap",
                }}
              >
                {headerCompany}
              </span>
            )}
          </div>
        )}

        {/* Left Video Screen */}
        <div
          style={{
            position: "absolute",
            top: `${
              currentVideoConfig.leftScreenPosition.y * containerScale
            }px`,
            left: `${
              currentVideoConfig.leftScreenPosition.x * containerScale
            }px`,
            width: `${currentVideoConfig.screenWidth * containerScale}px`,
            height: `${currentVideoConfig.screenHeight * containerScale}px`,
            overflow: "hidden",
            backgroundColor: "#000",
            zIndex: 1,
          }}
        >
          {videoUrl && (
            <video
              ref={leftVideoRef}
              src={videoUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: orientation === "1920x1080" ? "contain" : "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
              autoPlay
              muted
              playsInline
              loop
            />
          )}
        </div>

        {/* Right Video Screen */}
        <div
          style={{
            position: "absolute",
            top: `${
              currentVideoConfig.rightScreenPosition.y * containerScale
            }px`,
            left: `${
              currentVideoConfig.rightScreenPosition.x * containerScale
            }px`,
            width: `${currentVideoConfig.screenWidth * containerScale}px`,
            height: `${currentVideoConfig.screenHeight * containerScale}px`,
            overflow: "hidden",
            backgroundColor: "#000",
            zIndex: 1,
          }}
        >
          {videoUrl && (
            <video
              ref={rightVideoRef}
              src={videoUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: orientation === "1920x1080" ? "contain" : "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
              autoPlay
              muted
              playsInline
              loop
            />
          )}
        </div>

        {/* Frame Image */}
        <img
          src={frameImage}
          alt="display frame"
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
};

export default VideoPreview;
